// src/CBCLogin.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "./apiFetch";

export default function CBCLogin({
  onLogin = () => {},
  initialMode = "login", // "login" | "request-link" | "set-password"
  initialEmail = "",
  initialToken = "",
}) {
  const [email, setEmail] = useState(() => String(initialEmail || ""));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // OTP / Set-password mode
  const [mode, setMode] = useState(() =>
    initialMode === "set-password" ? "set-password" : "login"
  );

  // Token persisted in sessionStorage so a page refresh doesn't lose it
  const [token, setTokenRaw] = useState(() => {
    return sessionStorage.getItem("cbc_pwd_token") || String(initialToken || "");
  });
  const setToken = useCallback((v) => {
    const s = String(v || "");
    setTokenRaw(s);
    if (s) sessionStorage.setItem("cbc_pwd_token", s);
    else sessionStorage.removeItem("cbc_pwd_token");
  }, []);
  const [linkSentMsg, setLinkSentMsg] = useState("");

  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const emailLc = useMemo(() => email.trim().toLowerCase(), [email]);

  const validateEmail = (e) => /.+@.+\..+/.test(String(e || "").trim());
  const validateNewPw = (pw) => {
    if (!pw || pw.length < 10) return "Use at least 10 characters";
    if (!/[a-z]/.test(pw)) return "Include a lowercase letter";
    if (!/[A-Z]/.test(pw)) return "Include an uppercase letter";
    if (!/\d/.test(pw)) return "Include a number";
    if (!/[^A-Za-z0-9]/.test(pw)) return "Include a special character (!@#$...)";
    return "";
  };

  const showServerError = (err) => {
    const raw = String(err?.message || err || "");

    if (/PASSWORD_NOT_SET/i.test(raw)) {
      setMode("request-link");
      setError("");
      setLinkSentMsg("");
      return;
    }

    if (/NetworkError|Failed to fetch|CORS/i.test(raw)) {
      setError("Can’t reach the server. Check the API is running and CORS settings.");
      return;
    }

    setError(raw || "Login failed");
  };

  const doLogin = useCallback(
    async (emailLcArg, pwArg) => {
      const e = String(emailLcArg || "").trim().toLowerCase();
      const pw = String(pwArg || "");

      if (!validateEmail(e) || !pw) {
        throw new Error("Missing email or password");
      }

      if (process.env.NODE_ENV !== "production") {
        console.log("LOGIN payload =>", { email: e, password: pw ? "••••••••" : "" });
      }

      const data = await apiFetch("/api/login", {
        method: "POST",
        body: { email: e, password: pw },
      });

      if (!data?.ok || !data?.user?.email) {
        throw new Error(data?.error || "Login failed");
      }

      localStorage.setItem("cbc_user", JSON.stringify(data.user || {}));
      onLogin?.(data);
      return data;
    },
    [onLogin]
  );

  async function handleLogin(e) {
    e?.preventDefault?.();
    if (loading) return;

    setError("");
    setLinkSentMsg("");

    const eNorm = String(email || "").trim().toLowerCase();
    const pwNorm = String(password || "");

    if (!validateEmail(eNorm)) return setError("Enter a valid email");
    if (!pwNorm) return setError("Enter your password");

    try {
      setLoading(true);
      await doLogin(eNorm, pwNorm);
    } catch (err) {
      console.error("❌ Login error:", err);
      showServerError(err);
    } finally {
      setLoading(false);
    }
  }

  async function requestPasswordLink() {
    setPwErr("");
    setError("");
    setLinkSentMsg("");

    const eNorm = String(email || "").trim().toLowerCase();
    if (!validateEmail(eNorm)) {
      setPwErr("Enter a valid email");
      return;
    }

    try {
      setPwLoading(true);
      await apiFetch("/api/request-password-setup", {
        method: "POST",
        body: { email: eNorm },
      });

      setLinkSentMsg("If your email has access, we’ve sent a setup link. Please check your inbox.");
    } catch (err) {
      console.error("❌ request-password-setup error:", err);
      setLinkSentMsg("If your email has access, we’ve sent a setup link. Please check your inbox.");
    } finally {
      setPwLoading(false);
    }
  }

  async function submitSetPasswordWithToken() {
    setPwErr("");
    setError("");
    setLinkSentMsg("");

    const eNorm = String(email || "").trim().toLowerCase();
    if (!validateEmail(eNorm)) return setPwErr("Email is invalid");

    // Token is hidden; if missing we must tell the user to request a new link.
    if (!String(token || "").trim()) return setPwErr("Setup link is missing or invalid. Request a new link.");

    const vmsg = validateNewPw(newPw);
    if (vmsg) return setPwErr(vmsg);
    if (newPw !== newPw2) return setPwErr("Passwords do not match");

    try {
      setPwLoading(true);

      await apiFetch("/api/set-password", {
        method: "POST",
        body: { email: eNorm, token: String(token || "").trim(), password: newPw },
      });

      await doLogin(eNorm, newPw);

      setMode("login");
      setPassword("");
      setToken("");
      sessionStorage.removeItem("cbc_pwd_email");
      setNewPw("");
      setNewPw2("");
      setPwErr("");
    } catch (err) {
      console.error("❌ set-password error:", err);
      const msg = String(err?.message || "Error setting password");
      if (/expired|token/i.test(msg)) setPwErr("Invalid or expired setup link. Request a new one.");
      else setPwErr(msg);
    } finally {
      setPwLoading(false);
    }
  }

  // ✅ Parse URL for set-password link, store token in memory, then remove token from URL immediately
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const m = String(params.get("mode") || "").trim();
      const e = String(params.get("email") || "").trim();
      const t = String(params.get("token") || "").trim();

      const shouldSetPassword = m === "set-password" && !!t;

      if (process.env.NODE_ENV !== "production") {
        console.log("[CBCLogin init]", {
          initialMode,
          initialEmail,
          initialToken: initialToken ? "yes" : "no",
          urlMode: m,
          urlEmail: e,
          urlToken: t ? "yes" : "no",
          shouldSetPassword,
          href: window.location.href,
        });
      }

      if (shouldSetPassword) {
        setMode("set-password");
        if (e) setEmail(e);
        setToken(t);
        if (e) sessionStorage.setItem("cbc_pwd_email", e);
        setError("");
        setPwErr("");
        setLinkSentMsg("");

        // ✅ Immediately remove sensitive params from the URL (token/email/mode)
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("mode");
          url.searchParams.delete("email");
          url.searchParams.delete("token");
          window.history.replaceState({}, "", url.pathname + url.search);
        } catch {
          // ignore
        }
      } else if (initialMode === "set-password" && String(initialToken || "").trim()) {
        // honor wrapper props too
        setMode("set-password");
        setError("");
        setPwErr("");
        setLinkSentMsg("");

        // If wrapper provided token but URL also has it, still strip it
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.get("token")) {
            url.searchParams.delete("mode");
            url.searchParams.delete("email");
            url.searchParams.delete("token");
            window.history.replaceState({}, "", url.pathname + url.search);
          }
        } catch {
          // ignore
        }
      } else if (sessionStorage.getItem("cbc_pwd_token")) {
        // Restore set-password mode after page refresh
        setMode("set-password");
        const savedEmail = sessionStorage.getItem("cbc_pwd_email");
        if (savedEmail) setEmail(savedEmail);
        setError("");
        setPwErr("");
        setLinkSentMsg("");
      }
    } catch {
      // ignore
    }
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Enter" && (loading || pwLoading)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [loading, pwLoading]);

  const isLogin = mode === "login";
  const isRequestLink = mode === "request-link";
  const isSetPassword = mode === "set-password";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white shadow-xl rounded-2xl p-6">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold">CBC Portal Login</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSetPassword
              ? "Set your password to continue"
              : isRequestLink
              ? "Request a password setup link"
              : "Sign in to continue"}
          </p>
        </div>

        {/* LOGIN MODE */}
        {isLogin && (
          <>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-blue-600 text-white py-2.5 font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="mt-4 text-center space-y-2">
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline"
                onClick={() => {
                  setMode("request-link");
                  setError("");
                  setLinkSentMsg("");
                  setPwErr("");
                }}
                disabled={loading}
              >
                Forgot / Set password
              </button>
              <br />
              <a
                href="/signup"
                className="text-sm text-blue-600 hover:underline"
              >
                Become a Dealer
              </a>
            </div>
          </>
        )}

        {/* REQUEST LINK MODE */}
        {isRequestLink && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pwLoading}
                autoComplete="email"
                placeholder="you@company.com"
              />
            </div>

            {pwErr && <div className="text-sm text-red-600">{pwErr}</div>}
            {linkSentMsg && <div className="text-sm text-green-700">{linkSentMsg}</div>}
            {error && <div className="text-sm text-red-600">{error}</div>}

            <button
              type="button"
              onClick={requestPasswordLink}
              disabled={pwLoading}
              className="w-full rounded-lg bg-blue-600 text-white py-2.5 font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {pwLoading ? "Sending…" : "Email me a setup link"}
            </button>

            <div className="text-center">
              <button
                type="button"
                className="text-sm text-gray-600 hover:underline"
                onClick={() => {
                  setMode("login");
                  setPwErr("");
                  setError("");
                  setLinkSentMsg("");
                }}
                disabled={pwLoading}
              >
                Back to login
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              If your email has access, you’ll receive a link to set your password.
            </p>
          </div>
        )}

        {/* SET PASSWORD MODE */}
        {isSetPassword && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pwLoading}
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">New password</label>
              <input
                type="password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Min 10 chars, upper + lowercase, number & special char"
                disabled={pwLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm password</label>
              <input
                type="password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={newPw2}
                onChange={(e) => setNewPw2(e.target.value)}
                disabled={pwLoading}
              />
            </div>

            {pwErr && <div className="text-sm text-red-600">{pwErr}</div>}
            {error && <div className="text-sm text-red-600">{error}</div>}

            <button
              type="button"
              onClick={submitSetPasswordWithToken}
              disabled={pwLoading}
              className="w-full rounded-lg bg-blue-600 text-white py-2.5 font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {pwLoading ? "Saving…" : "Set password & sign in"}
            </button>

            <div className="text-center">
              <button
                type="button"
                className="text-sm text-gray-600 hover:underline"
                onClick={() => {
                  setMode("request-link");
                  setPwErr("");
                  setError("");
                  setLinkSentMsg("");
                  setToken("");
                  setNewPw("");
                  setNewPw2("");
                }}
                disabled={pwLoading}
              >
                Request a new link
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Passwords must be at least 10 characters and include uppercase, lowercase, a number, and a special character.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
